















































import AudienceCreation from './AudienceCreation.vue'
import AudiencePicker from './AudiencePicker.vue'
import AudienceInfo from '../AudienceInfo.vue'
import SplitView from '@/components/SplitView.vue'
import { format } from '@/utilities/audience-helpers.js'
import preQualificationTypes from '@/utilities/pq-types'
import { Vue, Prop, Component } from 'vue-property-decorator'
import api from '@/api'
import store from '@/store'
import * as db from '@shared/types/database'
import * as graph from '@shared/types/graphql'
import analytics from '@/analytics'
import { companyAudiences, featuredAudiences, USAdultsAudience } from '@/utilities/audience-mappers'
import { enableOrganizationWorkspaces } from '@/config'

export enum Stages {
  LIBRARY = 'LIBRARY',
  CREATION = 'CREATION'
}

(preQualificationTypes as any)['Additional Criteria'] = {
  type: 'no type'
}

export interface Notifications {
  errors?: string
  info?: string
  success?: string
}

export interface AudienceLibraryEntry {
  title: string
  description?: string
  type?: string
  audiences: Partial<db.Audience>[]
}

export interface AudienceEmission {
  audience: Partial<db.Audience>,
  groupType?: string
}

@Component({
  components: {
    AudienceCreation,
    AudiencePicker,
    AudienceInfo,
    SplitView
  }
})
export default class AudienceBuilder extends Vue {
  @Prop({ default: 'builder' }) mode!: string
  @Prop({ required: true }) stage!: string
  @Prop({ default: null }) accountAudiences!: graph.Audience[]
  @Prop({ required: true }) workspaceId!: string
  @Prop({ required: true }) organizationId!: string
  @Prop({ required: true }) accountId!: string
  @Prop({ default: null }) testAudience!: Partial<db.Audience> | null
  @Prop({ default: '' }) cloneAudienceId!: string

  saving = false
  selectedAudience: Partial<db.Audience> | null = null
  preQualErrors = {}
  notifications: Notifications = {}

  get workspaceAudiences () {
    // TODO Remove this once organization-level Audiences land
    // https://app.clubhouse.io/alpha-hq/story/7442/ui-work
    if (!enableOrganizationWorkspaces) {
      return this.accountAudiences
    }

    return this.accountAudiences.filter(a => a.payload.organization_id === this.organizationId)
  }

  get companyAudiences () {
    return companyAudiences(this.workspaceAudiences)
  }

  get featuredAudiences () {
    return featuredAudiences(this.workspaceAudiences)
  }

  get USAdultsAudience () {
    return USAdultsAudience(this.workspaceAudiences)
  }

  get Stages () {
    return Stages
  }

  get audienceLibrary () {
    let audienceLibrary: AudienceLibraryEntry[]
    if (this.workspaceAudiences.length > 0) {
      audienceLibrary = [{
        title: 'Featured Audiences',
        description: '<b>Featured Audiences</b> reflect some of the most frequently-accessed test participant types used by Feedback Loop clients.',
        audiences: this.featuredAudiences
      },
      {
        title: 'All Audiences',
        description: '<b>All Audiences</b> are available to all workspaces across your organization',
        audiences: this.companyAudiences
      }]
    } else {
      audienceLibrary = [
        {
          title: 'Featured Audiences',
          audiences: [this.USAdultsAudience]
        }
      ]
    }
    return audienceLibrary
  }

  mounted () {
    if (this.testAudience && Object.keys(this.testAudience).length > 0) {
      this.updateSelectedAudience({ selectedAudience: this.testAudience })
    } else if (this.cloneAudienceId) {
      const clonedAudience = this.featuredAudiences.concat(this.companyAudiences).find(audience => audience.id === this.cloneAudienceId)
      if (clonedAudience) {
        this.updateSelectedAudience({ selectedAudience: clonedAudience })
      }
    }
    this.scrollToSelectedAudience()
  }

  createCustomAudience () {
    this.$emit('clearAudience')
    this.$emit('setStage', Stages.CREATION)
  }

  showNextButton () {
    return false
  }

  updateSelectedAudience ({ selectedAudience, groupType }: { selectedAudience: Partial<db.Audience>, groupType?: string }) {
    this.selectedAudience = selectedAudience
    const emission: AudienceEmission = { audience: selectedAudience, groupType }
    this.$emit('setAudience', emission)
  }

  cancel () {
    this.$emit('setStage', Stages.LIBRARY)
    this.resetErrors()
  }

  resetErrors () {
    this.notifications.errors = undefined
  }

  async saveWorkspaceAudience (payload: { formattedAudienceForRR: db.FormattedAudienceForRR }) {
    const newAudienceInfo = await api.createAudience(this.workspaceId, payload.formattedAudienceForRR)
    const newAudience = await api.getAudienceById(newAudienceInfo.data.id, this.accountId!)
    store.commit.workspace.addAccountAudience(newAudience)
    return newAudienceInfo
  }

  async saveCustomPrequal (payload: { audienceName: string, criteria: db.Criteria, errors: string, reciever: any }) {
    const { audienceName, criteria, errors, reciever } = payload
    if (this.saving) return

    this.$set(this, 'notifications', {})

    if (errors) {
      this.notifications = { errors }
      this.saving = false
      reciever.finish()
      return
    }

    const audienceNames = this.audienceLibrary.reduce((acc, aud) => {
      return acc.concat(aud.audiences.map(a => {
        return (a.title && a.title.toLowerCase().replace(/ /g, '')) || ''
      }))
    }, [] as string[])
    if (audienceNames.indexOf(audienceName.toLowerCase().replace(/ /g, '')) !== -1) {
      this.notifications = { errors: 'Audience name already exists.' }
      this.saving = false
      reciever.finish()
      return
    }

    const formattedAudienceForRR = {
      name: audienceName,
      segment: format.forRR(criteria) as db.Segment
    }

    this.notifications = { info: 'Saving Audience...' }
    this.saving = true

    try {
      const { data } = await this.saveWorkspaceAudience({ formattedAudienceForRR })
      const lucidKeysUsed = Object.keys(formattedAudienceForRR.segment.lucid)
      const metrics = {
        workspaceId: this.workspaceId,
        additional: !!formattedAudienceForRR.segment.custom,
        international: !!formattedAudienceForRR.segment.countries
      } as any
      lucidKeysUsed.forEach((key) => {
        metrics[key] = true
      })

      if (formattedAudienceForRR.segment.custom) lucidKeysUsed.push('additional')
      if (formattedAudienceForRR.segment.countries) lucidKeysUsed.push('international')
      metrics.demographicsUsed = lucidKeysUsed

      analytics.track('Save Custom Audience', metrics)

      const selectedAudience: Partial<db.Audience> = {
        id: data.id,
        title: audienceName,
        criteria: format.translate(preQualificationTypes, criteria, { lucid: false })
      }
      this.notifications = { success: 'Audience saved.' }
      this.saving = false
      reciever.finish()
      this.updateSelectedAudience({ selectedAudience, groupType: 'Account' })
      this.$emit('setStage', Stages.LIBRARY)
      this.resetErrors()
      setTimeout(() => {
        this.notifications = {}
        this.scrollToSelectedAudience()
      }, 10)
    } catch (err) {
      console.error('Failed saving audience:', err)
      this.notifications = { errors: 'There was an issue saving your audience. Try again. If problem persists, contact your CSM.' }
      this.saving = false
      reciever.finish()
    }
  }

  scrollToSelectedAudience () {
    if (!this.$refs.audiencePicker) return
    const audiencePicker = this.$refs.audiencePicker as Vue
    const audiencePickerEl = audiencePicker.$el
    if (!audiencePickerEl.querySelector) return
    const selectedListItem = audiencePickerEl.querySelector('.list-item.selected') as HTMLElement | null
    if (!selectedListItem) return
    const scrollContainer = audiencePickerEl.parentElement
    if (!scrollContainer) return
    const offsetTop = selectedListItem.offsetTop
    scrollContainer.scrollTop = offsetTop
  }
}
